import { useState, useRef, useCallback, useEffect, useMemo } from "react"
import { nowEpochSeconds, unstaleFormStart, msUntilFormOldEnough } from "../lib/botProtection"

// The three anonymous-write signals (honeypot, timing stamp, Turnstile token)
// and — the reason this is shared rather than inlined three times — the two
// things every form that sends them has to get right:
//
//   1. The recovery after the server refuses a submission (#166). Every
//      anonymous write surface (#create behind QrBuilder, #email behind
//      EmailCapture and MobileSaveSheet) runs the same server-side
//      `bot_signal_tripped`, so they have the same dead ends and must recover
//      the same way.
//   2. Not sending what the server can only refuse (#339). From the moment the
//      widget mounts until its first callback there is no token at all, and a
//      form younger than the timing check's minimum fails that check. Either
//      way a fast person who pastes a link and presses at once is refused, with
//      copy that blames their VPN. So a press is held until the check settles
//      AND the form is old enough, then sent once. The same holds while a reset
//      re-issues the challenge.
//
// Usage: spread `turnstileProps` into <Turnstile>, wire `honeypot` /
// `setHoneypot` to the hidden input, send through `submitWhenReady(send)`,
// spread `fields()` into the API call, and call `recover()` on a
// `verification_failed` response. `turnstilePending` (the check is running)
// and `held` (a press is waiting on the check or the minimum) drive the
// submit button.
export default function useBotProtection() {
  const [honeypot, setHoneypotState] = useState("")
  // Mirrors of the values `fields()` sends. A held press runs a send function
  // created at press time; reading refs, it still sends the token that ended
  // the wait rather than the empty one in that render's closure.
  const honeypotRef = useRef("")
  const tokenRef = useRef(null)
  // Bumped after a bot-layer refusal to force a fresh Turnstile challenge; the
  // old token is spent or expired, so retrying with it can only fail again.
  const [turnstileResets, setTurnstileResets] = useState(0)
  // True while the widget is checking and there is no token: from mount until
  // the first callback, while a reset is in flight, and while an expired token
  // refreshes. Never true without a widget on the page (no site key, or a
  // caller that renders no <Turnstile>). See Turnstile's onPending.
  const [turnstilePending, setPendingState] = useState(false)
  const pendingRef = useRef(false)
  // A press made too soon, waiting to be sent.
  const [held, setHeld] = useState(false)
  const heldSend = useRef(null)
  // Bumped when a held press's form reaches the minimum age, to re-run the
  // send effect.
  const [ageTick, setAgeTick] = useState(0)
  // Stamped once at mount so the server's timing check sees the real
  // open-to-submit delta (not a re-render-induced reset).
  const formStartedAt = useRef(nowEpochSeconds())

  const setHoneypot = useCallback((value) => {
    honeypotRef.current = value
    setHoneypotState(value)
  }, [])

  const setTurnstileToken = useCallback((token) => { tokenRef.current = token }, [])

  const setTurnstilePending = useCallback((pending) => {
    pendingRef.current = pending
    setPendingState(pending)
  }, [])

  // The widget is gone (the form closed). A press held for it is dropped, not
  // sent: the person closed the form, and a widget mounted later starts its own
  // check.
  const onTurnstileGone = useCallback(() => {
    heldSend.current = null
    setHeld(false)
    setTurnstilePending(false)
  }, [setTurnstilePending])

  // Sends now, or holds the press until the check settles and the form is old
  // enough for the server's timing check. Only one press is ever held: the
  // button is disabled while `held`, and a second call replaces the first
  // rather than queueing behind it.
  const submitWhenReady = useCallback((send) => {
    if (!pendingRef.current && msUntilFormOldEnough(formStartedAt.current) === 0) return send()
    heldSend.current = send
    setHeld(true)
  }, [])

  // Sends the held press once both hold: the check settled (a token arrived,
  // the widget errored, or Turnstile's safety valve gave up waiting) and the
  // form has reached the timing check's minimum age. Without a token the
  // server has the last word: a real failed challenge is refused as before,
  // and a request carrying the QA bypass (#320) goes through.
  useEffect(() => {
    if (turnstilePending || !heldSend.current) return
    const wait = msUntilFormOldEnough(formStartedAt.current)
    if (wait > 0) {
      const id = setTimeout(() => setAgeTick((n) => n + 1), wait)
      return () => clearTimeout(id)
    }
    const send = heldSend.current
    heldSend.current = null
    setHeld(false)
    send()
  }, [turnstilePending, held, ageTick])

  const fields = useCallback(() => ({
    honeypot: honeypotRef.current,
    formStartedAt: formStartedAt.current,
    turnstileToken: tokenRef.current
  }), [])

  // Clears every signal a *human* can be stuck behind, because the server's
  // rejection is deliberately generic — it never says which of the three fired,
  // so the retry has to be able to survive any of them.
  const recover = useCallback(() => {
    setTurnstileResets((n) => n + 1)
    // A password manager or an over-eager autofill can populate the hidden
    // honeypot. Left alone, every retry resubmits the same trip.
    setHoneypot("")
    formStartedAt.current = unstaleFormStart(formStartedAt.current)
  }, [setHoneypot])

  const turnstileProps = useMemo(() => ({
    onToken: setTurnstileToken,
    onPending: setTurnstilePending,
    onGone: onTurnstileGone,
    resetSignal: turnstileResets
  }), [setTurnstileToken, setTurnstilePending, onTurnstileGone, turnstileResets])

  return {
    honeypot,
    setHoneypot,
    turnstileResets,
    turnstilePending,
    held,
    turnstileProps,
    submitWhenReady,
    formStartedAt,
    fields,
    recover
  }
}
